import React, { useEffect, useState, useRef } from 'react'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { useForm, Controller } from 'react-hook-form'
import Select from 'react-select'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { useDispatch, useSelector } from 'react-redux'
import { selectDropdowns, selectDropdownOptions } from '../../../redux/dropdownSlice'
import { setChangeMade } from '../../../redux/paymentSchedulePanelSlice'
import ManagerApprovalIcons from '../../../components/elements/ManagerApprovalIcons'
import PromoTag from "../../../components/elements/PromoTag";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'

export default function DiscountPayment(props) {
  const { loanId, refreshPaymentSchedule, navigateSlider, currentDate, paymentId, promotions } = props
  const requesting = useRef(false)
  const request = useRequest()
  const dispatch = useDispatch()
  const [responseData, setResponseData] = useState([])
  const { handleSubmit, control, watch, setValue, formState: { isDirty, dirtyFields } } = useForm();
  const [saving, setSaving] = useState(false)
  const dropdowns = useSelector(selectDropdowns);
  const dropdownOptions = useSelector(selectDropdownOptions);
  const [totalAmount, setTotalAmount] = useState(0);
  const discountReason = dropdowns.data.filter((o) => o.code_key === 'discount_reason')[0];
  const discountReasonOptions = dropdownOptions.filter((o) => o.dropdown_id === discountReason.id && o.optional_value_1 === 'TRUE').map((o) => ({ label: o.name, value: o.id }));
  const watchedFields = watch(['late_fee', 'nsf_fee', 'loan_fee', 'principal', 'interest']);
  const watchDiscountReason = watch('discount_reason_dropdown_option_id');
  const [requiresManagerApproval, setRequiresManagerApproval] = useState(false);
  const [discountType, setDiscountType] = useState("amount")
  const [discountSign, setDiscountSign] = useState("$")
  const [discountInfoText, setDiscountInfoText] = useState("")

  useEffect(() => {
    // Calculate the sum whenever any field changes
    if (discountType === "amount") {
      const sum = Object.values(watchedFields).reduce((acc, cur) => acc + parseFloat(cur || 0), 0);
      setTotalAmount(sum);
    } else if (discountType === "percent") {
      const percentLateFee = (parseFloat(watchedFields[0] || 0) / 100) * (responseData?.max_late_fee || 0);
      const percentNsfFee = (parseFloat(watchedFields[1] || 0) / 100) * (responseData?.max_nsf_fee || 0);
      const percentLoanFee = (parseFloat(watchedFields[2] || 0) / 100) * (responseData?.max_loan_fee || 0);
      const percentPrincipal = (parseFloat(watchedFields[3] || 0) / 100) * (responseData?.max_principal || 0);
      const percentInterest = (parseFloat(watchedFields[4] || 0) / 100) * (responseData?.max_interest || 0);

      const total = percentPrincipal + percentInterest + percentLateFee + percentNsfFee + percentLoanFee;
      setTotalAmount(total);
    }

    if (responseData?.max_discount_without_approval) {
      setRequiresManagerApproval(responseData?.requires_manager_approval && (totalAmount > responseData.max_discount_without_approval));
    }
    const hasChanged = Object.keys(watchedFields).some((key) => {
      return watchedFields[key] != undefined;
    });
    dispatch(setChangeMade(hasChanged))
  }, [watchedFields]);

  useEffect(() => {
    Object.keys(responseData).forEach((value) => {
      if (value.startsWith('current_')) {
        const fieldName = value.slice(8);
        if (!dirtyFields[fieldName]) {
          setValue(fieldName, Number.parseFloat(responseData[value]).toFixed(2));
        }
      }
    });
  }, [responseData, setValue]);

  useEffect(() => {
    getDiscountPayment(loanId)
  }, [])

  useEffect(() => {
    getDiscountPayment(loanId, watchDiscountReason?.value)
  }, [watchDiscountReason])

  const getDiscountPayment = async (loanId, discountReasonId) => {
    if (requesting.current) return;
    requesting.current = true;
    try {
      let endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment/${paymentId}/discount_info`;
      // Check if we have discount_reason_dropdown_option_id
      if (discountReasonId !== undefined) {
        endpoint += `?discount_reason_dropdown_option_id=${discountReasonId}`;
      }
      // Check if the currentDate variable has a value and, if so, add it as a query parameter
      if (currentDate !== null) {
        if ((discountReasonId !== undefined)) {
          endpoint += '&';
        } else {
          endpoint += '?';
        }
        endpoint += `current_date=${currentDate}`;
      }
      request.setEndpoint(endpoint)
      const response = await request.get()
      setResponseData(response?.data?.data);
      requesting.current = false;
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
      requesting.current = false;
    }
  }

  const onSubmit = async (formData) => {
    setSaving(true)
    // Check if the late fee and NSF fee are both zero
    if (parseFloat(formData.late_fee) === 0
      && parseFloat(formData.nsf_fee) === 0
      && parseFloat(formData.principal) === 0
      && parseFloat(formData.interest) === 0
      && parseFloat(formData.loan_fee) === 0) {
      setSaving(false)
      dispatch(setNotification({ type: 'error', body: "At least one of the field should have value" }))
      return;
    }
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment/${paymentId}/discount`)
    try {
      if (currentDate !== null) {
        formData['current_date'] = currentDate;
      }

      // change the paramater name to match the API
      if (discountType === "amount") {
        formData.late_fee_amount = formData.late_fee;
        formData.nsf_fee_amount = formData.nsf_fee;
        formData.loan_fee_amount = formData.loan_fee;
        formData.principal_amount = formData.principal;
        formData.interest_amount = formData.interest;
      } else if (discountType === "percent") {
        formData.late_fee_percent = formData.late_fee;
        formData.nsf_fee_percent = formData.nsf_fee;
        formData.loan_fee_percent = formData.loan_fee;
        formData.principal_percent = formData.principal;
        formData.interest_percent = formData.interest;
      }

      // unset late_fee, nsf_fee, loan_fee, principal, interest
      delete formData.late_fee;
      delete formData.nsf_fee;
      delete formData.loan_fee;
      delete formData.principal;
      delete formData.interest;

      formData.discount_reason_dropdown_option_id = formData.discount_reason_dropdown_option_id.value;
      const response = await request.modify(formData)
      if (response.status === 200 || response.status === 201) {
        setSaving(false)
        dispatch(setNotification({ type: 'success', body: '' }))
        navigateSlider("form_submit")
        refreshPaymentSchedule()
      } else {
        setSaving(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  };

  const checkChangesMade = () => {
    navigateSlider("");
  }

  const winbackCheck = () => {
    const winback = (promotions && promotions.length > 0 && promotions.find((promotion) => promotion.is_winback === 1));
    if (winback) {
      return (
        <><PromoTag promoType="winback" /> {' - '} <span className="is-size-7">{winback.description}</span> </>
      )
    }
    return null;
  }

  useEffect(() => {
    if (discountType === "amount") {
      setDiscountSign("$")
      setDiscountInfoText("Discounting by amount selected")
    } else if (discountType === "percent") {
      setDiscountSign("%")
      setDiscountInfoText("Discounting by percentage selected")
    }
  }, [discountType])

  return (
    <div className="pl-5 py-3">
      <form onSubmit={handleSubmit(onSubmit)} id="discount_payment_form">
        <p className="title is-5 mb-0">Discount Payment {paymentId} {requiresManagerApproval && <ManagerApprovalIcons type="mgr_approval_status_required" />}</p>
        <span>
          {winbackCheck()}
        </span>
        <table className="table is-narrow table-payment-schedule-panel">
          <tbody>
            <tr>
              <td>Amount $</td>
              <td>{totalAmount.toFixed(2)}</td>
              <td>Reason $</td>
              <td style={{ width: "200px" }}>
                <Controller
                  name="discount_reason_dropdown_option_id"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={discountReasonOptions}
                      isSearchable={false}
                      classNames={reactSelectClassNames()}
                      onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      menuPosition="fixed"
                      form="discount_payment_form"
                      styles={{ menu: ({ ...css }) => ({ ...css, width: 'max-content' }) }}
                    />
                  )}
                />
              </td>
            </tr>
          </tbody>
        </table>

        <table className="table is-narrow table-payment-schedule-panel">
          <tbody>
            <tr>
              <td className="pt-5" colSpan={10}>
                <b>Discount Type</b>: <span className="has-text-primary">{discountInfoText}</span>
                <div>
                  <div className="control">
                    <label className="radio mr-3">
                      <input name="discount_type" value="amount" type="radio" onChange={() => setDiscountType("amount")} checked={discountType === "amount"} /> Amount ($)
                    </label> OR <label className="radio ml-3">
                      <input name="discount_type" value="percent" type="radio" onChange={() => setDiscountType("percent")} checked={discountType === "percent"} /> Percent (%)
                    </label>
                  </div>
                </div>
                <p></p>
              </td>
            </tr>
            <tr>
              <td>Principal {discountSign}</td>
              <td style={{ width: '150px' }}>
                <Controller
                  name="principal"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="number"
                      form="discount_payment_form"
                      min={0}
                      max={discountType == 'amount' ? responseData?.max_principal : 100}
                      step="0.01"
                      disabled={!responseData?.max_principal}
                    />
                  )}
                />
              </td>
              <td>Interest {discountSign}</td>
              <td style={{ width: '150px' }}>
                <Controller
                  name="interest"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="number"
                      form="discount_payment_form"
                      min={0}
                      max={discountType == 'amount' ? responseData?.max_interest : 100}
                      step="0.01"
                      disabled={!responseData?.max_interest}
                    />
                  )}
                />

              </td>
              <td>Late Fee {discountSign}</td>
              <td style={{ width: '150px' }}>
                <Controller
                  name="late_fee"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="number"
                      form="discount_payment_form"
                      min={0}
                      max={discountType == 'amount' ? responseData?.max_late_fee : 100}
                      step="0.01"
                      disabled={!responseData?.max_late_fee}
                    />
                  )}
                />
              </td>
              <td>NSF Fee {discountSign}</td>
              <td style={{ width: '150px' }}>
                <Controller
                  name="nsf_fee"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="number"
                      form="discount_payment_form"
                      min={0}
                      max={discountType == 'amount' ? responseData?.max_nsf_fee : 100}
                      step="0.01"
                      disabled={!responseData?.max_nsf_fee}
                    />
                  )}
                />
              </td>
              <td>Loan Fee {discountSign}</td>
              <td style={{ width: '150px' }}>
                <Controller
                  name="loan_fee"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="number"
                      form="discount_payment_form"
                      min={0}
                      max={discountType == 'amount' ? responseData?.max_loan_fee : 100}
                      step="0.01"
                      disabled={!responseData?.max_loan_fee}
                    />
                  )}
                />
              </td>
            </tr>
          </tbody>
        </table>

        <table className="table is-narrow table-payment-schedule-panel">
          <tbody>
            <tr>
              {requiresManagerApproval ? <td colSpan={8}>NOTE: Discounts must be approved by management</td> : <td colSpan={8}></td>}
              <td>
                <div className="has-text-right">
                  <button
                    type="button"
                    className="button has-background-grey has-text-white is-small mr-1"
                    onClick={() => checkChangesMade()}
                  >
                    <span className="mx-3">Cancel</span>
                  </button>
                  <button
                    type="submit"
                    className="button is-link is-small mr-1"
                    disabled={saving && 'disabled'}
                  >
                    <span className="mx-3">{saving ? 'Discounting' : 'Discount'}</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </form>
    </div>
  )
}
